import PropTypes from 'prop-types';
import { createContext, useMemo } from 'react';
import { useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import useTableFilter from '@/hooks/useTableFilter';
import { getRatingsService } from '@/services/ratings';

export const RatingsDefaultFilter = {
  startDate: '',
  endDate: '',
  score: '',
  status: '',
};

export const ProductRatingsContext = createContext({
  title: 'Avaliações de Clientes',
  ratings: [],
  total: 0,
  averageRating: 0,
  table: {},
  fetching: false,
});

const ProductRatingsProvider = ({ children }) => {
  
  const { id: productId } = useParams();

  const table = useTableFilter({
    defaultCurrentPage: 1,
    tableProps: {
      defaultRowsPerPage: 10,
    },
    defaultFilter: RatingsDefaultFilter,
  });

  const { data: response = { results: [], count: 0 }, isFetching: fetching } = useQuery({
    queryKey: ['ratings', productId, table.page, table.rowsPerPage, table.filter],
    queryFn: () =>
      getRatingsService({
        page: table.page,
        limit: table.rowsPerPage,
        productId,
        filters: table.filter,
      }),
    keepPreviousData: true,
    initialData: {
      results: [],
      count: 0,
    },
  });

  const ratings = useMemo(() => response?.results || [], [response]);
  const total = response?.count || 0;

  const averageRating = useMemo(() => {
    if (!ratings.length) return 0;
    const sum = ratings.reduce((acc, item) => acc + (item.score || item.rating || 0), 0);
    return sum / ratings.length;
  }, [ratings]);

  const value = useMemo(
    () => ({
      title: 'Avaliações de Clientes',
      ratings,
      total,
      averageRating,
      table,
      fetching,
    }),
    [ratings, total, averageRating, table, fetching]
  );

  return <ProductRatingsContext.Provider value={value}>{children}</ProductRatingsContext.Provider>;
};

ProductRatingsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ProductRatingsProvider;
